import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import './Modal.scss';

import Icon from '../Icon/Icon';
import Button from '../../atoms/Button/Button';
import Image from '../Image/Image';

const Modal = ({
  backButton,
  children,
  className,
  close,
  closeIcon,
  contentClassName,
  contentRef,
  customModalWrapper,
  header,
  logo,
  newsroomInfoModal,
  onBack,
  size,
  title,
  titleClassName
}) => {
  const modalContentRef = useRef(null);
  const wrapperRef = useRef(null);
  const containerClasses = classNames(contentClassName, {
    'tmx-modal__content': size === 'small',
    'tmx-modal__content--large': size !== 'small',
    'tmx-modal__content--large--withoutHeader': size !== 'small' && !header,
    'tmx-modal__content--withBackAndHeader': backButton && header,
    'tmx-modal__content--withBack': backButton && !header,
    'tmx-modal__content--fullHeight': newsroomInfoModal
  });

  const closeCallback = useCallback(() => {
    if (close) {
      close();
    }
  }, [close]);

  const backCallback = useCallback(() => {
    if (onBack) {
      onBack();
    }
  }, [onBack]);

  return (
    <section className={classNames(className, 'tmx-modal')}>
      <div
        className={classNames('tmx-modal__wrapper', customModalWrapper, {
          'tmx-modal__wrapper--large': size === 'large'
        })}
        ref={wrapperRef}>
        {header ? (
          <div className="tmx-modal__header">
            <div className="tmx-modal__header-name">{header}</div>
            <div className="tmx-modal__close-wrapper">
              <Image src="/images/logo-line.webp" alt="logo-line" width="100%" height="100%" />
              <Icon
                name="close"
                size="micro"
                className="tmx-modal__close-wrapper__close"
                onClick={closeCallback}
              />
            </div>
          </div>
        ) : null}
        {closeIcon ? (
          <div className="tmx-modal__header__closeIcon">
            <Icon name="close" size="small" className="tmx-modal__close" onClick={closeCallback} />
          </div>
        ) : null}

        {backButton ? (
          <div className="tmx-modal__header__backButton">
            <Button
              buttonIcon="chevron"
              iconSize="medium"
              className="tmx-modal__back"
              handleClick={() => backCallback()}>
              {' '}
              {backButton}
            </Button>
          </div>
        ) : null}
        {!header && logo && (
          <div className="tmx-modal__logoContainer">
            <Icon
              name="tmx-brand-mark-beta-centered"
              size="large"
              className="tmx-modal__logoWrapper logo"
            />
          </div>
        )}
        {title && (
          <div
            className={classNames('tmx-modal__title', titleClassName)}
            dangerouslySetInnerHTML={{ __html: title }}></div>
        )}

        <div className={containerClasses} ref={contentRef || modalContentRef}>
          {children}
        </div>
      </div>
    </section>
  );
};

Modal.propTypes = {
  backButton: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  close: PropTypes.func,
  closeIcon: PropTypes.bool,
  contentClassName: PropTypes.string,
  contentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.instanceOf(Element)
    })
  ]),
  customModalWrapper: PropTypes.string,
  header: PropTypes.string,
  logo: PropTypes.bool,
  newsroomInfoModal: PropTypes.bool,
  onBack: PropTypes.func,
  size: PropTypes.oneOf(['small', 'large']),
  title: PropTypes.string,
  titleClassName: PropTypes.string,
  vertical: PropTypes.bool
};

Modal.defaultProps = {
  size: 'small'
};

export default Modal;
